/**
 * This is the part of the software responsible for
 * importing and exporting data.
 * This includes saving, loading and converting to and from other formats.
 */

import { unref } from "vue";
import { cloneDeep, omit } from "lodash-es";

import envVars from "../helpers/Env";
import { i18n } from "@/i18n";
import { getMigrations } from "@editor/store/migrations/index.js";
import {
  AccessDeniedError,
  BadRequestError,
  ModelLimitReachedError,
  ModelNotReadableError
} from "@core/errors/Errors.js";

import type { Model } from "./Model";
import { ensureInt, type WithRequired } from "../helpers/utility/Type";
import type { ModelVersion } from "./ModelVersion";
import type { ItemStoreState } from "@editor/items/ItemStore";
import type { StoreItem } from "@editor/items/StoreItem";

// other helpers
import { downloadBlob } from "../helpers/data/Download.js";
import ObjectToDocument from "../helpers/data/ObjectToDocument.js";
import Compressor from "../helpers/data/Compressor.js";
import Encryptor from "../helpers/data/Encryptor.js";
import eventHub from "@/helpers/utility/Events";
import { didAnythingChange } from "@/helpers/data/UnloadGuard";

import { blobToDataURL, blobToString } from "../helpers/utility/ReadFile.js";
import { useLoadedModel } from "@editor/store/loadedModel.store";

import { showAlert, showError, showMessage } from "@core/ui/alert/alert";
import { useSavePopup } from "@core/ui/SavePopup/SavePopup";

import { useUser } from "@/api/user.store";
import { useModels } from "@/api/models.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useModelStore } from "@editor/items/model/model.store";
import { useImageStore } from "@editor/items/image/images.store";
import { useUndo } from "@editor/store/undo/undo.store";
import { useProjectStore } from "@editor/store/project.store";
import { useMediaStore } from "@core/media/media.store";
import { getRouteToModelData } from "@core/routes/routeDetector.js";
import { defaultsDeepPreserveArrays } from "@/helpers/utility/CollectionFunctions";
import { migrateItemStore } from "@editor/items/itemStoreMigration";
import { vensimToJSON } from "@/helpers/data/vensim2simcision";
import { useLicense } from "./license.store";

type StoreState = {
  _meta?: {
    appVersion: string;
    savedAt?: number;
    locale?: string;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [storeId: string]: ItemStoreState | { [key: string]: any } | undefined;
};

// modules in this list are not beeing saved.
const MODULES_TO_IGNORE = ["undo", "ui"];

const APP_VERSION = envVars.version;

// ===== SAVING ======

/**
 * Save the project file. Ask for name and password.
 * @param options.asNewModel should a new project be created? true when using saveAs
 */
export async function saveModel(
  options: {
    asNewModel?: boolean;
    isSimulationGame?: boolean;
    shouldRouteUpdate?: boolean;
  } = {}
) {
  const { shouldRouteUpdate = true } = options;
  const { asNewModel = false } = options;
  let { isSimulationGame = false } = options;

  if (
    document.activeElement &&
    "blur" in document.activeElement &&
    typeof document.activeElement.blur == "function"
  ) {
    document.activeElement.blur?.();
  }

  // prevent saving if simulating
  if (useSimulationViewStore().simulating) {
    showMessage(i18n.t("messages.waitUntilSimulationEnd"));
    throw new Error(i18n.t("messages.waitUntilSimulationEnd"));
  }

  const userStore = useUser();
  const user = await userStore.getOrLoadUser();

  const { loadedModel, updateLoadedModel, setLoadedModel } = useLoadedModel();

  const modelInfo: Partial<Model> = {
    id: loadedModel.id,
    name: loadedModel.name,
    description: loadedModel.description,
    folder: loadedModel.folder,
    workspace: loadedModel.workspace ?? user?.workspace?.id,
    lastVersion: loadedModel?.lastVersion,
    isSimulationGame,
    permissions: loadedModel?.permissions
  };

  // save as a read-only project:
  if (isSimulationGame) {
    // make sure we move to simulation.
    eventHub.emit("route-change-request", { path: "simulation" });

    if (didAnythingChange()) {
      const saveBeforeExport = await showAlert({
        title: i18n.t("labels.saveChanges"),
        content: [
          {
            type: "text",
            text: i18n.t("questions.saveChanges")
          }
        ],
        confirmText: i18n.t("labels.save"),
        denyText: i18n.t("labels.dontSave")
      });

      if (saveBeforeExport) await saveModel();
    }
  }

  let password = Encryptor.password;
  let author;

  const projectStore = useProjectStore();

  // Business games should ONLY be able to be saved as
  // new file.
  if (projectStore.readOnly) {
    isSimulationGame = true;
    author = projectStore.author;
    modelInfo.isSimulationGame = true;
  }

  if (asNewModel) {
    // When saving as new model, pass in the current model id
    // to the save process.
    // That model id will be used to allow reuse of any
    // existing media connected to the previous model.
    modelInfo.ancestorModelType = loadedModel?.type ?? "workspace_model";
    modelInfo.ancestorModel = modelInfo.id;

    // clear the data that is directly related to THIS model.
    modelInfo.name = undefined;
    modelInfo.id = undefined;
    modelInfo.permissions = undefined;

    projectStore.createdAt = Date.now();

    // Remove the password info if we're saving as a new
    // project.
    password = undefined;
    Encryptor.reset();
  }

  // Get name and other modelInfos if not set:
  if (modelInfo.name == null || modelInfo.name === "") {
    // ask for all input data
    try {
      const { openSavePopup } = useSavePopup();
      const saveOptions = await openSavePopup({
        name: loadedModel.name + i18n.t("labels.copySuffix"),
        workspace: modelInfo.workspace,
        folder: modelInfo.folder,
        folderSlug: modelInfo.folderSlug,
        isSimulationGame: modelInfo.isSimulationGame,
        author: author
      });

      modelInfo.name = saveOptions.name;
      modelInfo.workspace = saveOptions.workspace;
      modelInfo.folder = saveOptions.folder;

      modelInfo.folderSlug = saveOptions.folderSlug;

      if (saveOptions.password) {
        password = saveOptions.password;
        Encryptor.password = password;
      }

      // save the new information to the project store.
      const info: {
        readOnly: boolean;
        author?: string;
        exportedModelName?: string;
      } = {
        readOnly: !!modelInfo.isSimulationGame
      };

      // additional data if saved as simulation
      if (saveOptions.author) {
        author = saveOptions.author;
        
        info.author = author;
        info.exportedModelName = modelInfo.name;
        
        projectStore.author = info.author ?? null;
        projectStore.exportedModelName = info.exportedModelName ?? null;
      }

      if ("readOnly" in info) {
        projectStore.readOnly = info.readOnly;
      }
    } catch (error) {
      // nothing entered? cancel.
      console.log(error);
      throw new Error("Canceled.");
    }
  }

  const filledModelInfo = modelInfo as WithRequired<
    Model,
    "name" | "workspace" | "folder"
  >;

  if (filledModelInfo.id == null) {
    // Create a model ID before saving (EVERY model needs a model id)

    // Model data can only be saved online if there is
    // a model entry associated to it in the cloud.
    // So, we need to create that first, before trying
    // to save this modeldata.
    try {
      const { createModel } = useModels();
      const newModelInfo = await createModel(filledModelInfo);

      const loadedLastVersion = { ...(loadedModel?.lastVersion ?? {}) };

      Object.assign(filledModelInfo, newModelInfo);

      // Restore the last version info (when saving as new, we can base the version on the last).
      filledModelInfo.lastVersion = loadedLastVersion;

      setLoadedModel(newModelInfo);

      if (shouldRouteUpdate) {
        const newRoute = getRouteToModelData(loadedModel);
        eventHub.emit("model-route-update", newRoute);
      }

      console.log("new model info", { ...filledModelInfo });
    } catch (_error) {
      const error = _error as Error & { code?: string };

      if (error?.code === "model_limit_reached") {
        await showError(new ModelLimitReachedError(useLicense().limits.maxModels ?? 0));
      } else if (
        error instanceof BadRequestError ||
        error instanceof AccessDeniedError
      ) {
        console.log(
          "bad request save",
          error.message,
          error.status,
          error.code
        );

        // ABORT saving if there was an error saving
        // the model in the cloud
        await showAlert({
          title: i18n.t("errors.creating"),
          confirmText: i18n.t("labels.ok"),
          content: [
            {
              type: "text",
              text: error.message
            }
          ]
        });
        throw new Error(i18n.t("errors.creating"));
      }


      throw error;
    }
  }

  try {
    const [updatedModelInfo, updatedModelData] = await saveToCloud(
      filledModelInfo,
      {
        password
      }
    );

    console.log("updated model info", { ...updatedModelInfo });

    // Save the new ID to modelInfo module
    updateLoadedModel(updatedModelInfo);

    if (shouldRouteUpdate) {
      const newRoute = getRouteToModelData(loadedModel);
      eventHub.emit("model-route-update", newRoute);
    }

    // notify the user
    await showAlert({
      title: i18n.t("headings.saved"),
      content: [
        {
          type: "text",
          text: i18n.t("messages.savedX", [updatedModelInfo.name])
        }
      ]
    });

    return {
      modelInfo: updatedModelInfo,
      modelData: updatedModelData
    };
  } catch (_error) {
    const error = _error as Error;
    console.error(error);

    // saving not possible: error message
    await showAlert({
      title: i18n.t("headings.saving"),
      content: [
        {
          type: "text",
          text: i18n.t("errors.savingX", [filledModelInfo.name])
        },
        {
          type: "text",
          text: error.message
        }
      ]
    });
    throw new Error(i18n.t("headings.saving"));
  }
}

/**
 * Save the current state of the project in a project file
 * (locally if offline, in cloud when online)
 * @param options.password a password for encryption
 */
export async function saveToCloud(
  modelInfo: Model,
  { password }: { password?: string | null } = {}
) {
  // set the time of the last change to now
  modelInfo.modified = new Date().toISOString();

  // make sure there's a workspace id field in the modelInfo
  if (modelInfo?.workspace == null) {
    const { getOrLoadUser } = useUser();
    const user = await getOrLoadUser();
    modelInfo.workspace = user.workspace.id;
  }

  const modelId = modelInfo?.id;
  const workspaceId = modelInfo?.workspace;

  console.log(`saving ${modelId} to cloud workspace ${workspaceId}`);

  // show a loading indicator
  const loadingIndicatorCancelToken = { cancel: (_k: unknown) => undefined };
  showAlert(
    {
      title: i18n.t("headings.savingData"),
      confirmText: undefined,
      content: [
        {
          type: "text",
          text: i18n.t("messages.savingModel")
        }
      ]
    },
    loadingIndicatorCancelToken
  );

  try {
    const {
      updateModel,
      getModelInfo,
      updateModelData,
      getModelData,
      synchModel
    } = useModels();

    // Get the newest state of the model information
    // from the platform
    // (the title and the description could have
    // been changed in the backend)
    const freshModelInfo = await getModelInfo(modelId);

    let updatedModelInfo = modelInfo;

    // if the model info changed, update it.
    if (
      freshModelInfo.name != modelInfo.name ||
      freshModelInfo.description != modelInfo.description ||
      freshModelInfo.folder != modelInfo.folder ||
      freshModelInfo.workspace != modelInfo.workspace
    ) {
      updatedModelInfo = await updateModel(modelInfo, {
        synchAfterwards: false
      });
    }

    const {
      data: encryptedModelData,
      media,
      newMediaUUIDs
    } = getOpenModelData(password);

    // Save the model version data:
    const updatedModelData = await updateModelData(
      {
        ...updatedModelInfo,
        lastVersion: modelInfo.lastVersion
      },
      {
        media,
        newMediaUUIDs,
        data: encryptedModelData,
        appVersion: APP_VERSION
      },
      { isPlayerProject: useProjectStore().readOnly }
    );

    if (useUser().isSyncEnabled()) {
      await synchModel(modelId)
        .then((synchedModel) => {
          console.log("synched model", synchedModel);
          // TODO: Update open model!!
        })
        .catch((error) => {
          console.error("error on model synch", error);
        });
    }

    // If the returned modelData contains different data than we sent,
    // replace the loaded data!
    // (this only happens when new data arrived during save)
    if (updatedModelData.data != encryptedModelData) {
      console.warn("REPLACE DATA");
      const dataWithMedia = await getModelData(
        updatedModelInfo.workspace,
        updatedModelInfo.id
      );
      await loadInfoAndData(modelInfo, dataWithMedia);
    }

    console.log("updated model data", updatedModelData);

    const { setLoadedModel, updateLoadedModel } = useLoadedModel();

    // If the returned modelData has a different model id,
    // update that modelInfo.
    // (This happens if the model had to be changed to a new one, on a save conflict)
    if (updatedModelData.model != modelInfo.id) {
      updatedModelInfo = await getModelInfo(updatedModelData.model);
      setLoadedModel(updatedModelInfo);
    }

    updatedModelInfo = updateLoadedModel({
      lastVersion: { id: updatedModelData.id }
    });

    const result: [Model, ModelVersion] = [updatedModelInfo, updatedModelData];
    eventHub.emit("model-saved", result);
    return result;
  } catch (error) {
    console.error("Fehler beim Speichern:Datenabfrage.", error);
    throw error;
  } finally {
    // remove the loading indicator
    loadingIndicatorCancelToken.cancel(true);
  }
}

/**
 * Get the currently OPEN model data
 * @param password The password to encrypt the current model data with.
 */
export function getOpenModelData(password?: string | null) {
  // If a password is passed, the data will be encrypted.
  const ENCRYPTED = password != null;

  const cleanedModelData = formatForSaving(useModelStore().modelState);

  if (!ENCRYPTED) {
    // If the model is not securely encrypted,
    // save the media seperately. This reduces
    // server load and storage space.
    delete cleanedModelData.media;
  }

  const encryptedModelData = Encryptor.encrypt(
    cleanedModelData,
    password ?? undefined
  );

  const mediaStore = useMediaStore();

  return {
    data: encryptedModelData,

    /**
     * All media items
     */
    media: ENCRYPTED ? [] : mediaStore.saveableEntries,

    /**
     * All new media files
     */
    newMediaUUIDs: ENCRYPTED ? [] : mediaStore.newMediaUUIDs
  };
}

// ====== LOADING =========

export async function loadInfoAndData(
  modelInfo: Partial<Model>,
  modelData: {
    data?: string | null;
    id?: number | string | null;
    media?: { uuid: string; data: Blob | string }[];
  },
  options: { updateMediaReferences?: boolean } = {}
) {
  Encryptor.reset();

  let dataToLoad: StoreState;

  if (modelData.data == null) {
    console.warn("Creating new model.");

    // load an empty model...
    dataToLoad = {};
  } else {
    // Decrypt that data
    const decryptedData = await Encryptor.decryptAskingForPassword(
      modelData.data
    );

    if (!decryptedData) {
      throw new ModelNotReadableError();
    }

    dataToLoad = decryptedData as StoreState;
  }

  console.log("permissions", modelInfo.permissions);

  modelInfo.lastVersion = { id: modelData.id };

  await loadData(dataToLoad, {
    media: modelData.media
  }, options);

  const { setLoadedModel } = useLoadedModel();

  setLoadedModel(modelInfo);
}

/**
 * When loading a model, nearly the whole pinia store is beeing reset.
 */
export async function loadData(
  data: StoreState,
  dataMeta: { media?: { uuid: string; data: Blob | string }[] } = {},
  options: { updateMediaReferences?: boolean } = {}
) {
  const modelStore = useModelStore();

  // clean up current state
  modelStore.reset();
  useUndo().disable();

  console.log("data to load", cloneDeep(data));

  const loadedStoreState = await getLoadableStoreState(
    data,
    modelStore.modelState
  );

  modelStore.replaceModel(
    loadedStoreState as { [storeId: string]: ItemStoreState<StoreItem> }
  );

  // load media entries
  const mediaEntries = dataMeta?.media ?? [];
  const mediaStore = useMediaStore();
  const imageStore = useImageStore();
  for (const mediaEntry of mediaEntries) {
    let data: string;

    if (mediaEntry.data instanceof Blob) {
      data = await blobToDataURL(mediaEntry.data);
    } else {
      data = mediaEntry.data;
    }

    const stringMediaEntry = {
      uuid: mediaEntry.uuid,
      data
    };

    mediaStore.add(stringMediaEntry);
  }

  await imageStore.createMediaEntries();

  // replace all media uuids on load when importing any simcision model file
  // (the references in those files can't be trusted)
  if (options.updateMediaReferences) {
    await imageStore.markImagesAsNew();
  }

  console.log("loaded store state", loadedStoreState);

  eventHub.emit("model-loaded", loadedStoreState);

  useModelStore().setupUndo();
  useUndo().enable();

  return true;
}

/**
 * Transfer data from a state copy to the live state of the app.
 * @param data a js object mirroring the model store
 * @param initialRootState the state the new data should be written into:
 * @param overwriteAfter a list of properties that should be overridden
 */
export async function getLoadableStoreState(
  data: StoreState,
  initialRootState: StoreState,
  overwriteAfter: StoreState = {}
) {
  if (data == null) {
    console.error("no data to load");
    return initialRootState;
  }

  if (!isSaveVersionCompatible(data)) {
    await showError(
      i18n.t("errors.savedWithLaterVersion", [data._meta?.appVersion])
    );
    throw new Error(
      "SAVE_INCOMPATIBLE: Save file was saved with newer version."
    );
  }

  // delete ignored modules
  for (const moduleName of MODULES_TO_IGNORE) {
    if (data[moduleName] != null) {
      delete data[moduleName];
    }
  }

  // remove _id and _rev from local DB
  delete data._id;
  delete data._rev;

  // remove meta data
  const metaData = data._meta;
  if (metaData) {
    delete data._meta;
  }
  const isNew = data.games == null; // old versions (without a version number) had a "games" module
  const versionSavedWith = metaData?.appVersion || (isNew ? null :  "1.0.0");

  console.log("metadata of savefile", metaData, versionSavedWith);

  const migrations = {
    before: getMigrations("before", versionSavedWith),
    after: getMigrations("after", versionSavedWith)
  };

  const dataBefore = cloneDeep(data);

  // make minimal store structure preparations
  for (const moduleName in data) {
    const moduleData = data[moduleName];
    const rootModuleData = initialRootState[moduleName];

    if (rootModuleData == null) continue;
    if (moduleData == null) continue;

    if (rootModuleData.itemStructure) {
      // transfer the "itemStructure" property first
      moduleData.itemStructure = cloneDeep(rootModuleData.itemStructure);

      // remove undefined items
      for (let _id in (moduleData.items || {})) {
        const id = parseInt(_id);
        const item = moduleData.items[id];
        if (item == null) {
          delete moduleData.items[id];
          if (moduleData.order) {
            moduleData.order = moduleData.order.filter((i: number) => i !== id);
          }
          continue;
        }
      }

      // make sure the "order" property exists
      if (moduleData.order == null) {
        if (typeof moduleData.items === "object") {
          moduleData.order = Object.keys(moduleData.items).map(ensureInt);
        } else {
          moduleData.order = [];
        }
      } else {
        // ensure the order only contains existing items
        moduleData.order = moduleData.order.filter((id: number) =>
          Object.keys(moduleData.items).includes(id.toString())
        );
      }
    }
  }

  data = migrations.before(data, {
    rootState: initialRootState,
    oldData: dataBefore,
    versionBefore: versionSavedWith
  }) as StoreState;

  const rootState = cloneDeep(initialRootState);

  // combine new data with old, overwrite arrays.
  let newData: StoreState = {};
  for (const _moduleName in rootState) {
    const moduleName = _moduleName as keyof typeof rootState & string;

    const dataModule = data[moduleName];
    const rootModule = rootState[moduleName];

    // if the loaded data doesn't have the module, take the old one.
    if (dataModule == null) {
      newData[moduleName] = rootModule;
      continue;
    }

    // if it is a standard store, copy it
    if (rootState[moduleName]?.itemStructure == null) {
      const newDataModule: {
        [k in keyof typeof rootModule]: (typeof rootModule)[k];
      } = {};

      for (const _prop in rootModule) {
        const prop = _prop as keyof typeof rootModule;

        // if the property is NOT defined in the save
        // take the current prop of the rootState
        if (!(prop in dataModule)) {
          Object.assign(newDataModule, { [prop]: rootModule[prop] });
        }
        // if the property IS defined in the save state:
        else {
          let value = dataModule[prop];

          // Object? Clone it.
          if (isObject(value)) {
            value = mergeDeep(rootModule[prop], dataModule[prop]);
          }

          Object.assign(newDataModule, { [prop]: value });
        }
      }

      newData[moduleName] = newDataModule;
      continue;
    }

    // if it's an object store, copy the items
    // this will make sure that the old items have the new item structure.
    if (
      rootModule?.itemStructure != null &&
      typeof dataModule?.items === "object"
    ) {
      const migratedStore = await migrateItemStore(
        dataModule as ItemStoreState<StoreItem>,
        rootModule as ItemStoreState<StoreItem>
      );
      newData[moduleName] = migratedStore;
    }
  }

  // apply migrations after the data was loaded:
  newData = migrations.after(newData, {
    rootState: rootState,
    oldData: dataBefore,
    versionBefore: versionSavedWith
  }) as StoreState;

  // get the developerMode from the local storage
  if ("settings" in newData && newData.settings) {
    newData.settings.developerMode = JSON.parse(
      localStorage.getItem("developerModeActive") || "false"
    );
  }

  // Ensure the item order integrities:
  for (const moduleName in newData) {
    const moduleData = newData[moduleName];
    if (moduleData?.itemStructure == null) continue;
    if (moduleData.items == null) continue;
    if (moduleData.order == null) continue;
    
    // ensure the order only contains existing items
    moduleData.order = moduleData.order.filter((id: number) =>
      Object.keys(moduleData.items).includes(id.toString())
    );

    // ensure the order is unique
    moduleData.order = moduleData.order.filter(onlyUnique);

    // add missing items to the order
    for (const itemId in moduleData.items) {
      const id = parseInt(itemId);
      if (!moduleData.order.includes(id)) {
        moduleData.order.push(id);
      }
    }
  }

  // Apply data overrides:
  for (const moduleName in overwriteAfter) {
    newData[moduleName] = defaultsDeepPreserveArrays(
      overwriteAfter[moduleName],
      newData[moduleName]
    );
  }

  return newData;
}

// ==== EXPORTING ====

/**
 * download an exported project
 */
export async function exportModel() {
  const { openSavePopup } = useSavePopup();
  const { loadedModel } = useLoadedModel();
  const currentPassword = Encryptor.password;
  const project = useProjectStore();

  // ask: save with password?
  const inputData = await openSavePopup(
    {
      name: loadedModel.name,
      isSimulationGame: project.readOnly,
      author: project.author,
      password: currentPassword
    },
    {
      asFileExport: true,
      showWorkspaceFolderInput: false
    }
  );

  if (!inputData) {
    return false;
  }

  console.log("exportInputData", inputData);

  const name = inputData.name || loadedModel.name;
  const password = inputData.password || null;
  const isSimulationGame = project.readOnly;

  const modelData = getOpenModelData(password);

  return downloadModelFile(loadedModel, modelData, {
    name,
    isSimulationGame
  });
}

/**
 * Export the state as file to download
 */
export async function downloadModelFile(
  modelInfo: Partial<Model>,
  modelData: Partial<ModelVersion>,
  { name = "Unbenannt", isSimulationGame = false }
) {
  if (modelData.data == null) {
    console.error("No data was passed.");
    return false;
  }

  const fileData = {
    _simcisionFileMeta: {
      fileVersion: "1.0"
    },
    info: {
      created: modelInfo.created,
      name: modelInfo.name,
      description: modelInfo.description
    },
    data: modelData.data,
    media: modelData.media
  };

  // search for blob entries
  const cleanedFileData = await replaceBlobsWithDataURLs(fileData);
  console.log("cleaned file data", cleanedFileData);

  const stringifiedValue = JSON.stringify(cleanedFileData);
  const blob = new Blob(["SIMCISIONMODEL.00001" + stringifiedValue], {
    type: "application/simcisionmodel"
  });
  console.log("datablob", blob);
  const fileName = `${name}.${isSimulationGame ? "sim" : "model"}`;
  downloadBlob(fileName, blob);
}

// ====== IMPORTING =======

/**
 * Load an encoded project from a File/Blob.
 * The file can be either a plain JSON file or an encoded simcision file.
 */
export async function importModel(
  file: File | Blob,
  modelInfo: Partial<Model>
) {
  const fileContent = await file.text();

  /**
   * The new simcision model files have a prefix that includes
   * the file version.
   */
  if (fileContent.startsWith("SIMCISIONMODEL.")) {
    // const fileVersion = fileContent.substring(15, 18);
    const fileData = JSON.parse(fileContent.substring(20));

    return loadInfoAndData(modelInfo, fileData, {
      updateMediaReferences: true
    });
  }

  // if the imported file is a vensim file, convert it to simcision
  else if ("name" in file && file.name.endsWith(".mdl") || file.type == "application/vensim") {
    if (alert != null) {
      alert("Importing vensim files is highly experimental. Not all features are supported, there might be errors.");
    }

    const data = vensimToJSON(fileContent);

    // throw new Error("Vensim files are not supported yet.");

    Encryptor.reset();
    const success = await loadData(data, {}, { updateMediaReferences: true });
    if (!success) {
      throw new Error("JSON could not be loaded");
    }
    const { setLoadedModel } = useLoadedModel();
    setLoadedModel(modelInfo);
  }

  // if the imported file is a json file, apply the old direct json import
  else if (file.type && file.type === "application/json") {
    const data = JSON.parse(fileContent);

    Encryptor.reset();
    const success = await loadData(data, {}, { updateMediaReferences: true });
    if (!success) {
      throw new Error("JSON could not be loaded");
    }
    const { setLoadedModel } = useLoadedModel();
    setLoadedModel(modelInfo);
  }

  // if it's not a json file, expect it to be a simcision file (encoded)
  else {
    // try to decompress data
    const encryptedModelData = {
      data: Compressor.decompress(fileContent)
    };

    return loadInfoAndData(modelInfo, encryptedModelData, {
      updateMediaReferences: true
    });
  }
}

/**
 * Import contents of a json file
 */
export async function importJSONFile(
  file: File | Blob,
  { createdAt = null }: { createdAt: string | null }
) {
  const fileContent = await blobToString(file);
  let data = null;

  // parse JSON string
  try {
    data = JSON.parse(fileContent);
  } catch (error) {
    console.error(error);
    await showError(i18n.t("errors.JSONnotLoadable"));
    throw new Error("JSON could not be decoded");
  }

  if (data == null) {
    console.error("file could not be read", data, file);
    throw new Error("File could not be read.");
  }

  // overwrite this project:
  if (createdAt) data.project.createdAt = createdAt; // rootState.project.createdAt;

  return loadData(data);
}

export function importJSONString(jsonString: string) {
  const data = JSON.parse(jsonString);
  if (data == null) return;

  return loadData(data);
}

// ==== HELPERS ====

function isObject(item: unknown) {
  return item && typeof item === "object" && !Array.isArray(item);
}
function onlyUnique(value: unknown, index: number, self: Array<unknown>) {
  return self.indexOf(value) === index;
}
/**
 * Returns a new object that contains a deep merge of the two objects
 */
function mergeDeep(
  defaults: Record<string, unknown>,
  values: Record<string, unknown>
) {
  const output = Object.assign({}, defaults);

  if (!isObject(defaults)) return values;
  if (!isObject(values)) return values;

  const keys = Object.keys(defaults)
    .concat(Object.keys(values))
    .filter(onlyUnique);

  for (const key of keys) {
    if (!(key in values) && key in defaults) {
      output[key] = defaults[key];
    } else if (isObject(values[key]) && isObject(defaults[key])) {
      output[key] = mergeDeep(
        defaults[key] as Record<string, unknown>,
        values[key] as Record<string, unknown>
      );
    } else {
      output[key] = values[key];
    }
  }

  return output;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function replaceBlobsWithDataURLs(obj: any): Promise<any> {
  if (obj instanceof Blob) {
    return await blobToDataURL(obj);
  }
  if (typeof obj !== "object") {
    return obj;
  }
  if (Array.isArray(obj)) {
    return await Promise.all(obj.map((item) => replaceBlobsWithDataURLs(item)));
  }

  const ret: { [k: string]: string } = {};
  for (const key in obj) {
    ret[key] = await replaceBlobsWithDataURLs(obj[key]);
  }
  return ret;
}

/**
 * Export the current file content as formatted document.
 */
export function exportAsDocument(rootState: StoreState) {
  let obj = formatForSaving(rootState);
  obj = omit(obj, [
    "selection",
    "net",
    "profile",
    "model",
    "templates",
    "impulses",
    "ways",
    "simulations",
    "modelView",
    "inspectorView",
    "variablesView",
    "simulationView",
    "evaluationView"
  ]);

  const text = ObjectToDocument(obj);
  console.log(obj);
  console.log(text);

  return text;
}

/**
 * Returns a cleaned up version of the rootState
 * that can be converted to JSON.
 * @param rootState pinia model store root state
 */
export function formatForSaving(rootState: StoreState) {
  const dataToSave = cloneDeep(rootState);

  // remove all modules that should not be saved:
  for (const moduleName of MODULES_TO_IGNORE) {
    if (dataToSave[moduleName] != null) {
      delete dataToSave[moduleName];
    }
  }

  // additional data for the save
  dataToSave._meta = {
    savedAt: Date.now(),
    appVersion: APP_VERSION,
    locale: unref(i18n.locale)
  };

  console.log("saving model state", dataToSave);

  return dataToSave;
}

/**
 * Pack a string into a blob
 */
export function toBlob(str: string) {
  return new Blob([str], {
    type: "application/octet-stream"
  });
}

/**
 * Check if the current version of the software can open the
 * model.
 */
export function isSaveVersionCompatible(modelInfo: StoreState) {
  if (modelInfo._meta == null) return true;
  const saveVersion = modelInfo._meta.appVersion
    .split(".")
    .map((num: string) => parseInt(num));
  const currentVersion = APP_VERSION.split(".").map((num) => parseInt(num));

  // saved with a later version, might be incompatible
  if (saveVersion[0] > currentVersion[0]) return false;
  if (saveVersion[0] == currentVersion[0] && saveVersion[1] > currentVersion[1])
    return false;

  return true;
}
